import { useDispatch } from 'react-redux'
import { complement, filter, propEq } from 'ramda'
import { forwardRef, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import Icon from '@/components/icon'
import { createModal, useModalize } from '@/components/modalize'
import VerifiedBadge from '@/components/verified-badge'
import UnverifiedBadge from '@/components/unverified-badge'
import isVerified from '@/utils/is-verified'
import { BlueClock, Warning, RedVerified } from '@/components/icons'
import { breakpoints, colors, fontWeights } from '@/styles'

const Wrapper = styled.div`
  background: ${colors.white};
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  max-height: 98vh;
  max-width: 767px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-height: unset;
    border-radius: 16px 16px 0 0;
  }
`

const ModalClose = styled.div`
  position: absolute;
  top: 19px;
  right: 25px;
  z-index: 1;

  svg {
    cursor: pointer;
    margin: 0;
  }

  :hover {
    opacity: 0.7;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    top: 20px;
    right: 20px;
  }
`

const AboutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${colors.white};
  padding: 26px 24px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
  }
`

const ProfileTitle = styled.h3`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: ${fontWeights.bold};
  border-bottom: 1px solid ${colors.grey};
  padding: 26px 50px 18px 24px;
  width: 100%;
`

const IconWrapper = styled.div`
  margin-right: 12px;
`

const StyledBlueClock = styled(BlueClock)`
  margin-right: 6px;
`

const PendingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: ${fontWeights.medium};
  font-size: 12px;
  background-color: ${colors.blue96};
  color: ${colors.blue32};
  padding: 4px 8px;
  border-radius: 8px;
  max-width: 152px;
`

const Text = styled.span`
  color: ${colors.grey16};
  font-size: 14px;
  font-weight: ${fontWeights.regular};
  line-height: 20px;
  width: 100%;
`

const SubtitleContainer = styled.div`
  display: flex;
  margin-bottom: 18px;
`

const Subtitle = styled.span`
  display: flex;
  align-items: center;
  color: ${colors.black};
  font-weight: ${fontWeights.medium};
  font-size: 16px;
  margin-right: 8px;
`

const MultiParagraphContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 20px;
  width: 100%;
  margin-top: ${props => (props.top ? props.top : 0)}px;
`

const Group = styled.div`
  ${props =>
    props.borderBottom &&
    `
      padding-bottom: 20px;
      border-bottom: 1px solid ${colors.grey88};
    `}
`

const Link = styled.a`
  color: ${colors.link};
  text-decoration: underline;
`

const VerifyActionText = styled.span`
  color: ${colors.link};
  text-decoration: underline;
  cursor: pointer;
`

const LearnMoreContainer = styled.div`
  margin-top: 24px;
  font-style: Italic;
`

const LandlordVerificationModal = forwardRef(
  ({ landlords, listingVerified = false, listingPending = false, timelineRoomId }, ref) => {
    const { close } = useModalize()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [requestingIdVerification, setRequestingIdVerification] = useState(false)
    const [requestingListingVerification, setRequestingListingVerification] = useState(false)

    const multilandlord = landlords?.length > 1
    const verifiedUsers = filter(user => isVerified(user, user?.company), landlords)
    const pendingUsers = filter(user => user?.identity_verified_state === 'pending', landlords)
    const allIdsVerified = verifiedUsers?.length === landlords?.length
    const unverified = !allIdsVerified || !listingVerified

    const handleRequestIdVerification = async () => {
      if (requestingIdVerification) return
      setRequestingIdVerification(true)
      const body = await dispatch.chat.requestIdVerification({
        timelineRoomId
      })
      if (body?.response?.ok) {
        setRequestingIdVerification(false)
      }
      close()
    }

    const handleRequestListingVerification = async () => {
      if (requestingListingVerification) return
      setRequestingListingVerification(true)
      const body = await dispatch.chat.requestListingVerification({
        timelineRoomId
      })
      if (body?.response?.ok) {
        setRequestingListingVerification(false)
      }
      close()
    }

    const renderIdParagraph = () => {
      if (allIdsVerified) {
        return multilandlord ? (
          <Text>
            {t(
              'r.chat.view.all_ids_verified_multiple.text',
              'We have verified that all landlords have provided government-issued IDs and selfies that match their profile information.'
            )}
          </Text>
        ) : (
          <Text>
            {t(
              'r.chat.view.all_ids_verified.text',
              'We have verified that this landlord has provided a government-issued ID and a selfie that matches their profile information.'
            )}
          </Text>
        )
      }

      const unverifiedLandlords = filter(
        complement(propEq('identity_verified_state', 'verified')),
        landlords
      )
      const multipleUnverified = unverifiedLandlords?.length > 1
      const getMultiLandlordString = () => {
        let landlordString = ``
        for (let i = 0; i <= unverifiedLandlords.length - 1; i++) {
          if (!multipleUnverified) {
            landlordString += `${unverifiedLandlords[i]?.first_name} ${unverifiedLandlords[
              i
            ]?.last_name?.substring(0, 1)}.`
          } else if (i === unverifiedLandlords.length - 1) {
            landlordString += `${unverifiedLandlords[i]?.first_name} ${unverifiedLandlords[
              i
            ]?.last_name?.substring(0, 1)}.`
          } else {
            landlordString += `${unverifiedLandlords[i]?.first_name} ${unverifiedLandlords[
              i
            ]?.last_name?.substring(0, 1)}., `
          }
        }
        return landlordString
      }

      const multiLandlordString = getMultiLandlordString()

      const unverifiedText = multilandlord ? (
        <Text>
          <Trans i18nKey="r.chat.view.ids_unverified_multiple.text">
            Our ID verification process verifies the identity of a user by matching their profile
            name <strong>and selfie</strong> with a piece of government-issued photo ID. As one or
            more landlords (<span>{{ multiLandlordString }}</span>) are unverified or pending
            review, please proceed with caution or request that those users{' '}
            <VerifyActionText onClick={handleRequestIdVerification}>
              verify their profile
            </VerifyActionText>
            .
          </Trans>
        </Text>
      ) : (
        <Text>
          <Trans i18nKey="r.chat.view.ids_unverified.text">
            Our ID verification process verifies the identity of a user by matching their profile
            name <strong>and selfie</strong> with a piece of government-issued photo ID. Since this
            user is unverified, please proceed with caution, or request that they{' '}
            <VerifyActionText onClick={handleRequestIdVerification}>
              verify their profile
            </VerifyActionText>
            .
          </Trans>
        </Text>
      )
      if (pendingUsers?.length > 0) {
        return multilandlord ? (
          unverifiedText
        ) : (
          <Text>
            {t(
              'r.chat.view.ids_pending_multiple.text',
              'This user has already submitted their ID, but is pending verification. Please check back within 2-3 business days.'
            )}
          </Text>
        )
      }

      return unverifiedText
    }

    const getIdBadge = () => {
      if (allIdsVerified) {
        return <VerifiedBadge type="id" width={24} height={24} verified={true} />
      }
      if (pendingUsers?.length > 0) {
        return (
          <PendingContainer>
            <StyledBlueClock />
            <span>{t('l.chat.view.pending.text', 'Pending')}</span>
          </PendingContainer>
        )
      }

      return <UnverifiedBadge width="unset" />
    }

    const renderListingParagraph = () => {
      if (listingVerified) {
        return (
          <Text>
            {t(
              'r.chat.view.listing_verified.text',
              'This rental property address has been verified by mail or by matching the landlord’s name to official documents, such as land title documents or property tax forms.'
            )}
          </Text>
        )
      }
      if (listingPending) {
        return (
          <Text>
            {t(
              'r.chat.view.listing_pending.text',
              'A request for listing verification has already been submitted, but is pending. Please check back within 2-3 days.'
            )}
          </Text>
        )
      }

      return (
        <Text>
          <Trans i18nKey="r.chat.view.listing_unverified.text">
            Our listing verification process confirms the address of the property via mail or proof
            of document. Since this property is unverified, please proceed with caution, or request
            that they{' '}
            <VerifyActionText onClick={handleRequestListingVerification}>
              verify their listing
            </VerifyActionText>
            .
          </Trans>
        </Text>
      )
    }

    const getListingBadge = () => {
      if (listingVerified) {
        return <RedVerified />
      }
      if (listingPending) {
        return (
          <PendingContainer>
            <StyledBlueClock />
            <span>{t('l.chat.view.pending.text', 'Pending')}</span>
          </PendingContainer>
        )
      }

      return <UnverifiedBadge showSmallOnMobile={false} width="unset" />
    }

    return (
      <Wrapper ref={ref}>
        <ModalClose>
          <Icon aria-label="Close" id="close" width={24} height={24} onClick={close} />
        </ModalClose>
        <ProfileTitle>
          {unverified && (
            <IconWrapper>
              <Warning />
            </IconWrapper>
          )}
          <span>
            {unverified
              ? t('r.chat.view.proceed_with_caution.title', 'Please proceed with caution')
              : t('r.chat.view.verification_completed.title', 'Verification Completed')}
          </span>
        </ProfileTitle>

        <AboutWrapper>
          <MultiParagraphContainer>
            <Group borderBottom>
              <SubtitleContainer>
                <Subtitle>{t('r.chat.view.id_verification.subtitle', 'ID Verification')}</Subtitle>
                {getIdBadge()}
              </SubtitleContainer>
              {renderIdParagraph()}
            </Group>
            <Group>
              <SubtitleContainer>
                <Subtitle>
                  {t('r.chat.view.listing_verification.subtitle', 'Listing Verification')}
                </Subtitle>
                {getListingBadge()}
              </SubtitleContainer>
              {renderListingParagraph()}
            </Group>
          </MultiParagraphContainer>
          <LearnMoreContainer>
            <Text>
              <Trans i18nKey="r.chat.view.learn_more.text">
                Learn more about our{' '}
                <Link
                  target="_blank"
                  href="https://liv.rent/blog/2019/10/rental-verification-process/"
                >
                  verification process
                </Link>
                .
              </Trans>
            </Text>
          </LearnMoreContainer>
        </AboutWrapper>
      </Wrapper>
    )
  }
)

LandlordVerificationModal.displayName = 'LandlordVerificationModal'

export const showLandlordVerificationModal = createModal(LandlordVerificationModal, {
  width: 'auto',
  maxWidth: '496px',
  flexGrow: true,
  border: '1px solid #d8d8d8',
  mobileAlign: 'flex-end',
  tabletFullWidth: true
})
